export interface GachaResultRow {
  rarity: string
  name: string
  color: string
}

export interface GachaOverlayOptions {
  title: string
  pitch: string
  loadingText: string
  pullLabel: string
  backLabel: string
  cancelLabel: string
  confirmLabel: string
  emptyText: string
  onPull: () => void
  onBack: () => void
  onClaimBattlepass: () => void
}

export interface GachaOverlayHandle {
  setEconomy: (line: string) => void
  setPity: (line: string, ready: boolean) => void
  setStatus: (text: string, tone?: 'good' | 'bad' | 'sub') => void
  setPullState: (enabled: boolean, busy: boolean) => void
  setProgress: (lines: string[]) => void
  setBattlepass: (line: string, trackLine: string, claimable: boolean) => void
  setResults: (rows: GachaResultRow[]) => void
  showConfirm: (message: string, onConfirm: () => void, onCancel?: () => void) => void
  closeConfirm: () => void
  destroy: () => void
}

export function mountGachaOverlay(options: GachaOverlayOptions): GachaOverlayHandle {
  const root = document.createElement('div')
  root.className = 'game-gacha-overlay'

  const panel = document.createElement('section')
  panel.className = 'game-gacha-panel'

  const header = document.createElement('header')
  header.className = 'game-gacha-header'
  const title = document.createElement('h1')
  title.textContent = options.title
  const pitch = document.createElement('p')
  pitch.textContent = options.pitch
  header.append(title, pitch)

  const economy = document.createElement('p')
  economy.className = 'game-gacha-economy'

  const pity = document.createElement('p')
  pity.className = 'game-gacha-pity'

  const status = document.createElement('p')
  status.className = 'game-gacha-status'

  const results = document.createElement('section')
  results.className = 'game-gacha-results'
  results.textContent = options.emptyText

  const progress = document.createElement('section')
  progress.className = 'game-gacha-progress'

  const bp = document.createElement('button')
  bp.type = 'button'
  bp.className = 'game-gacha-bp'
  bp.addEventListener('click', options.onClaimBattlepass)
  const bpMain = document.createElement('strong')
  const bpTrack = document.createElement('span')
  bp.append(bpMain, bpTrack)

  const actions = document.createElement('div')
  actions.className = 'game-gacha-actions'
  const pull = button(options.pullLabel, 'game-gacha-btn game-gacha-primary', options.onPull)
  const back = button(options.backLabel, 'game-gacha-btn', options.onBack)
  actions.append(pull, back)

  panel.append(header, economy, pity, status, results, progress, bp, actions)
  root.append(panel)
  document.body.append(root)

  let confirmRoot: HTMLDivElement | undefined

  const setStatus = (text: string, tone: 'good' | 'bad' | 'sub' = 'sub'): void => {
    status.textContent = text
    status.hidden = !text
    status.dataset.tone = tone
  }

  const closeConfirm = (): void => {
    confirmRoot?.remove()
    confirmRoot = undefined
  }

  setStatus(options.loadingText)

  return {
    setEconomy: (line) => {
      economy.textContent = line
      economy.hidden = !line
    },
    setPity: (line, ready) => {
      pity.textContent = line
      pity.hidden = !line
      pity.dataset.ready = ready ? '1' : '0'
    },
    setStatus,
    setPullState: (enabled, busy) => {
      pull.disabled = busy
      pull.dataset.enabled = enabled ? '1' : '0'
      pull.textContent = busy ? options.loadingText : options.pullLabel
    },
    setProgress: (lines) => {
      progress.innerHTML = ''
      progress.hidden = lines.length === 0
      for (const line of lines) {
        const p = document.createElement('p')
        p.textContent = line
        progress.append(p)
      }
    },
    setBattlepass: (line, trackLine, claimable) => {
      bpMain.textContent = line
      bpTrack.textContent = trackLine
      bp.hidden = !line && !trackLine
      bp.disabled = !claimable
      bp.dataset.claimable = claimable ? '1' : '0'
    },
    setResults: (rows) => {
      results.innerHTML = ''
      if (rows.length === 0) {
        results.textContent = options.emptyText
        return
      }
      const best = rows.find((r) => r.rarity === 'SSR') ?? rows.find((r) => r.rarity === 'SR')
      if (best) {
        const featured = document.createElement('article')
        featured.className = 'game-gacha-featured'
        featured.style.setProperty('--rarity', best.color)
        featured.innerHTML = `<span>${escapeHtml(best.rarity)}</span><strong>${escapeHtml(best.name)}</strong>`
        results.append(featured)
      }
      const grid = document.createElement('div')
      grid.className = 'game-gacha-grid'
      for (const row of rows) {
        const card = document.createElement('div')
        card.className = 'game-gacha-result'
        card.style.setProperty('--rarity', row.color)
        card.innerHTML = `<b>${escapeHtml(row.rarity)}</b><span>${escapeHtml(row.name)}</span>`
        grid.append(card)
      }
      results.append(grid)
    },
    showConfirm: (message, onConfirm, onCancel) => {
      if (confirmRoot) return
      confirmRoot = document.createElement('div')
      confirmRoot.className = 'game-gacha-confirm'
      const card = document.createElement('section')
      card.className = 'game-gacha-confirm-card'
      const copy = document.createElement('p')
      copy.textContent = message
      const actions = document.createElement('div')
      actions.className = 'game-gacha-confirm-actions'
      actions.append(
        button(options.cancelLabel, 'game-gacha-btn', () => {
          closeConfirm()
          onCancel?.()
        }),
        button(options.confirmLabel, 'game-gacha-btn game-gacha-primary', () => {
          closeConfirm()
          onConfirm()
        }),
      )
      card.append(copy, actions)
      confirmRoot.append(card)
      root.append(confirmRoot)
    },
    closeConfirm,
    destroy: () => {
      closeConfirm()
      root.remove()
    },
  }
}

function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.className = className
  btn.textContent = label
  btn.addEventListener('click', onClick)
  return btn
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
